import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Icon, Popup, Checkbox, Input } from "semantic-ui-react";
import { capitalize, formatMoneyWithCurrency } from "src/utils/formatter";
import useWindowDimensions from "src/utils/windowDimension";
import moment from "moment/moment";
import { UpdateCashflowCollapsed } from "src/graphql/users";
import { importOpenSidebar } from "src/redux/actions/openSidebar";
import { UpdateCashflowCategories } from "src/redux/actions/auth";
import LoadingSpinner from "src/components/general/js/spinnerLoading";
import "../../../../flujocaja(new)/style.scss";
import { CurrencyIcon } from "src/utils/currencyIcon";
import FeatherIcon from "feather-icons-react";
import './CashFlowHolding.scss'
import mergedIcon from '../../../../../assets/icons/merged_area_icon.svg';
import FlowScreenshotButton from "src/components/flujocaja(new)/FlowScreenshotButton";

const VIEW_TYPE = {
  day: "day",
  week: "week",
  month: "month",
};

export default function HoldingFlow({
  getFlow,
  flowStructure,
  view,
  setNameSelected,
  tableHeight,
  start,
  end,
  loading,
  loadingMsg,
  checkSwitch,
  setCheckSwitch,
  updateCompress,
  setUpdateCompress,
  showAll,
  isHolding = false,
  holdingCompanyIds = [],
  plus,
  less,
  handleInputChange,
  setMergeAreasModalData,
  deferredAreaConflicts = [],
  onOpenAreaConflictsModal,
  screenshotRef,
  screenshotPrepareRef,
  onScreenshotCapture,
}) {
  const findDeferredAreaConflict = (reportType, categoryId, field) => {
    if (!deferredAreaConflicts?.length || !field?.cashflowAreaId) return null;
    return deferredAreaConflicts.find((conflict) => {
      if (conflict.reportType !== reportType) return false;
      if (String(conflict.categoryId) !== String(categoryId)) return false;

      if ((conflict.areaRefs || []).some((ref) => ref?.startsWith?.(`${field.cashflowAreaId}::`))) {
        return true;
      }

      return String(conflict.areaId) === String(field.cashflowAreaId);
    });
  };

  const renderDeferredConflictWarning = (conflict) => {
    if (!conflict) return null;
    const distinctLabels = [...new Set((conflict.labels || []).map((label) => label).filter(Boolean))];
    const popupContent = (
      <div>
        <p style={{ margin: "0 0 6px" }}>
          Esta fila tiene un conflicto de consolidación: la misma área aparece con nombres distintos entre empresas.
        </p>
        {distinctLabels.length > 0 && (
          <p style={{ margin: "0 0 6px" }}>
            Nombres detectados: {distinctLabels.join(" · ")}
          </p>
        )}
        <p style={{ margin: 0 }}>
          Abre la revisión de conflictos para combinar o mantener separadas.
        </p>
      </div>
    );

    return (
      <Popup
        hoverable
        position="top center"
        size="small"
        inverted
        content={popupContent}
        trigger={
          <button
            type="button"
            className="holding-area-conflict-warning"
            aria-label="Conflicto de área pendiente de revisión"
            onClick={(event) => {
              event.stopPropagation();
              onOpenAreaConflictsModal?.();
            }}
          >
            <Icon name="exclamation triangle" />
          </button>
        }
      />
    );
  };
  const dispatch = useDispatch();
  const { cashflowCollapsedCategories } = useSelector((state) => state.auth);

  const { selectedCompany } = useSelector((state) => state.companyList);
  const [areasSelectedOnce, setAreasSelectedOnce] = useState([]);
  const [compressedAreas, setCompressedAreas] = useState(
    cashflowCollapsedCategories.cashflowCategories ? cashflowCollapsedCategories : [{ cashflowCategories: [] }]
  );

  const [expandedHoldingCategories, setExpandedHoldingCategories] = useState([]);
  const [expandedCompanyRows, setExpandedCompanyRows] = useState([]);
  const [autoExpandedInitialBalance, setAutoExpandedInitialBalance] = useState(false);
  const [tableWidth, setTableWidth] = useState();
  const [selectedRow, setSelectedRow] = useState();
  const [startDate, setStartDate] = start;
  const [endDate, setEndDate] = end;

  const { width } = useWindowDimensions();
  const { openSidebar } = useSelector((state) => state.openSidebar);

  const [reportType, setReportType] = useState();
  const [selectedCashFlowAreaId, setSelectedCashFlowAreaId] = useState();
  const [selectedCashFlowCategoryId, setSelectedCashFlowCategoryId] =
    useState();
  const [selectedIndex, setSelectedIndex] = useState();

  const getMergedAreaOriginalLabels = (mergedFrom = []) => {
    const labels = (mergedFrom || [])
      .map((ref) => {
        if (!ref) return "";
        const parsed = ref.toString().split("::");
        return parsed.length >= 3 ? parsed[2] : "";
      })
      .map((label) => label?.trim())
      .filter(Boolean);
    return [...new Set(labels)];
  };

  const renderMergedAreaPopupContent = (mergedFrom = []) => {
    const labels = getMergedAreaOriginalLabels(mergedFrom);
    if (labels.length === 0) return "Área combinada";
    return (
      <div className="tw-flex tw-flex-col tw-gap-1">
        <span className="tw-font-medium">Áreas originales</span>
        {labels.map((label) => (
          <span key={label}>- {label}</span>
        ))}
      </div>
    );
  };

  const updateCashflowCollapsed = UpdateCashflowCollapsed();

  const rowNameReservedWidth = 150;
  const projectedColumnWidth = 123;
  const periodColumnWidth = 125;
  const availableForPeriods = (tableWidth || 0) - rowNameReservedWidth - projectedColumnWidth;
  const tot = Math.floor(availableForPeriods / periodColumnWidth);
  const maxRowNameWidth = 150;
  const [flowCurrentStructure] = flowStructure || [];

  const [isFilterAreaFound, setIsFilterAreaFound] = useState()
  useEffect(() => {
    const totalWidth = document.getElementsByClassName("flowTableContainer");
    setTableWidth(totalWidth[0]?.clientWidth);

    if (openSidebar?.show == false) setSelectedRow();
  }, [width, openSidebar?.show]);

  useEffect(() => {
    if (!openSidebar?.show || !getFlow?.report || !openSidebar?.fieldDetail) return;
    const reports = getFlow.report?.[reportType];
    const selectedCashFlowCategory = reports?.find(
      (cashFlow) => cashFlow.cashflowCategoryId == selectedCashFlowCategoryId
    );
    const selectedCashFlowArea = selectedCashFlowCategory?.fields?.find(
      (cashFlowArea) => cashFlowArea.cashflowAreaId == selectedCashFlowAreaId
    );
    const value = selectedCashFlowArea?.values?.[selectedIndex];
    if (!value) return;

    // openFieldDetail(
    //   value,
    //   { selectedCashFlowArea },
    //   selectedIndex,
    //   selectedCashFlowCategoryId,
    //   reportType
    // );

    if (value?.documents?.length === 0) {
      dispatch(importOpenSidebar({ show: false }));
    }
  }, [getFlow]);

  useEffect(() => {
    const maxColumns = Math.max(6, tot);

    const lastViewDate = (value) => {
      return moment(startDate)
        .add(value, view)
        .endOf(view)
        .set("hours", 23)
        .set("minutes", 59)
        .set("seconds", 59)
        .format("YYYY-MM-DDTHH:mm:ss.SSS");
    };

    setEndDate(lastViewDate(maxColumns));
  }, [tot, view, startDate]);

  const getAllCategoryIds = () => {
    const income = getFlow?.report?.INCOME?.filter((f) => f?.cashflowCategoryId)?.map(
      (el) => el.cashflowCategoryId.toString()
    );
    const expense = getFlow?.report?.EXPENSE?.filter((f) => f?.cashflowCategoryId)?.map(
      (el) => el.cashflowCategoryId.toString()
    );
    return [...(income || []), ...(expense || [])];
  };

  const getAllAreaExpandKeys = () => {
    const keys = new Set();
    ["INCOME", "EXPENSE"].forEach((reportType) => {
      getFlow?.report?.[reportType]?.forEach((category) => {
        category?.fields?.forEach((field) => {
          if (!field?.companiesValues?.length) return;
          const companyKey = field.companiesValues
            .map((company) => company?.companyId)
            .filter(Boolean)
            .sort()
            .join("-");
          keys.add(`${companyKey}-${field.cashflowAreaId || "no-area-id"}`);
        });
      });
    });
    const initialBalanceRow = getFlow?.report?.RESUME?.find(
      (row) => row?.label === "Saldo inicial" && row?.companiesValues?.length
    );
    if (initialBalanceRow) {
      const companyKey = initialBalanceRow.companiesValues
        .map((company) => company?.companyId)
        .filter(Boolean)
        .sort()
        .join("-");
      keys.add(`saldo-inicial-${companyKey || "holding"}`);
    }
    return Array.from(keys);
  };

  // En holding, abrir el desglose de saldo inicial apenas lleguen companiesValues.
  useEffect(() => {
    if (!isHolding || autoExpandedInitialBalance) return;
    const initialBalanceRow = getFlow?.report?.RESUME?.find(
      (row) => row?.label === "Saldo inicial" && row?.companiesValues?.length
    );
    if (!initialBalanceRow) return;

    const companyKey = initialBalanceRow.companiesValues
      .map((company) => company?.companyId)
      .filter(Boolean)
      .sort()
      .join("-");
    const expandKey = `saldo-inicial-${companyKey || "holding"}`;
    setExpandedCompanyRows((prev) =>
      prev.includes(expandKey) ? prev : [...prev, expandKey]
    );
    setAutoExpandedInitialBalance(true);
  }, [isHolding, getFlow?.report?.RESUME, autoExpandedInitialBalance]);

  useEffect(() => {
    if (!screenshotPrepareRef) return;

    screenshotPrepareRef.current = {
      prepare: () =>
        new Promise((resolve) => {
          const saved = {
            expandedHoldingCategories: [...expandedHoldingCategories],
            expandedCompanyRows: [...expandedCompanyRows],
            compressedAreas: JSON.parse(JSON.stringify(compressedAreas)),
          };

          if (isHolding) {
            setExpandedHoldingCategories(getAllCategoryIds());
            setExpandedCompanyRows(getAllAreaExpandKeys());
          } else {
            const income = getFlow?.report?.INCOME?.filter((f) => f?.cashflowCategoryId)?.map(
              (el) => el?.cashflowCategoryId
            );
            const expense = getFlow?.report?.EXPENSE?.filter((f) => f?.cashflowCategoryId)?.map(
              (el) => el?.cashflowCategoryId
            );
            const allCompress = (income || []).concat(expense || []);
            const companyCompressed = compressedAreas?.map((company) => {
              if (company?.companyId === selectedCompany?.id) {
                return { ...company, cashflowCategories: allCompress };
              }
              return { ...company };
            });
            const exist = companyCompressed?.find(
              (f) => f?.companyId === selectedCompany?.id
            );
            if (!exist) {
              companyCompressed?.push({
                companyId: selectedCompany?.id,
                cashflowCategories: allCompress,
              });
            }
            setCompressedAreas(companyCompressed);
            dispatch(UpdateCashflowCategories(companyCompressed));
          }

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setTimeout(() => resolve(saved), 100);
            });
          });
        }),
      restore: (saved) => {
        if (isHolding) {
          setExpandedHoldingCategories(saved.expandedHoldingCategories);
          setExpandedCompanyRows(saved.expandedCompanyRows);
        } else {
          setCompressedAreas(saved.compressedAreas);
          dispatch(UpdateCashflowCategories(saved.compressedAreas));
        }
      },
    };

    return () => {
      screenshotPrepareRef.current = null;
    };
  }, [
    screenshotPrepareRef,
    isHolding,
    getFlow?.report,
    expandedHoldingCategories,
    expandedCompanyRows,
    compressedAreas,
    selectedCompany?.id,
    dispatch,
  ]);

  const compressAll = async ({ value }) => {
    if (isHolding) {
      if (value) {
        setExpandedHoldingCategories([]);
      } else {
        const allCategoryIds = getAllCategoryIds();
        setExpandedHoldingCategories(allCategoryIds);
        if (allCategoryIds.length > 0) {
          setAreasSelectedOnce((prev) => {
            const numericIds = getFlow?.report?.INCOME?.concat(getFlow?.report?.EXPENSE || [])
              ?.filter((f) => f?.cashflowCategoryId)
              ?.map((f) => f.cashflowCategoryId) || [];
            return [...new Set([...prev, ...numericIds])];
          });
        }
      }
      setExpandedCompanyRows([]);
      return;
    }

    if (value) {
      const companyCompressed = compressedAreas?.map((company) => {
        if (company?.companyId === selectedCompany?.id) {
          return { ...company, cashflowCategories: [] };
        }
        return { ...company };
      });
      const exist = companyCompressed?.find(
        (f) => f?.companyId === selectedCompany?.id
      );
      if (!exist)
        companyCompressed?.push({
          companyId: selectedCompany?.id,
          cashflowCategories: [],
        });
      setCompressedAreas(companyCompressed);
      dispatch(UpdateCashflowCategories(companyCompressed));
      const newData = companyCompressed?.find(
        (f) => f?.companyId === selectedCompany?.id
      );
      await updateCashflowCollapsed({
        companyId: newData?.companyId,
        categoryId: newData?.cashflowCategories,
      });
    }
    if (!value) {
      const income = getFlow?.report?.INCOME?.filter((f) => {
        if (!f?.cashflowCategoryId) return;
        // if (!f?.isVisible) return
        return f?.cashflowCategoryId;
      })?.map((el) => el?.cashflowCategoryId);
      const expense = getFlow?.report?.EXPENSE?.filter((f) => {
        if (!f?.cashflowCategoryId) return;
        // if (!f?.isVisible) return
        return f?.cashflowCategoryId;
      })?.map((el) => el?.cashflowCategoryId);
      const allCompress = income?.concat(expense);
      const companyCompressed = compressedAreas?.map((company) => {
        if (company?.companyId === selectedCompany?.id) {
          return { ...company, cashflowCategories: allCompress };
        }
        return { ...company };
      });
      const exist = companyCompressed?.find(
        (f) => f?.companyId === selectedCompany?.id
      );
      if (!exist)
        companyCompressed?.push({
          companyId: selectedCompany?.id,
          cashflowCategories: [],
        });
      setCompressedAreas(companyCompressed);
      dispatch(UpdateCashflowCategories(companyCompressed));
      const newData = companyCompressed?.find(
        (f) => f?.companyId === selectedCompany?.id
      );
      await updateCashflowCollapsed({
        companyId: newData?.companyId,
        categoryId: newData?.cashflowCategories,
      });
    }
  };

  useEffect(() => {
    if (isHolding) {
      setCheckSwitch(expandedHoldingCategories.length === 0);
      return;
    }
    if (
      compressedAreas?.find((f) => f?.companyId === selectedCompany?.id)
        ?.cashflowCategories?.length === 0
    ) {
      setCheckSwitch(true);
    }
    if (
      compressedAreas?.find((f) => f?.companyId === selectedCompany?.id)
        ?.cashflowCategories?.length > 0
    ) {
      setCheckSwitch(false);
    }
  }, [compressedAreas, isHolding, expandedHoldingCategories]);

  useEffect(() => {
    if (!updateCompress || loading || !getFlow?.report) return;
    compressAll({ value: checkSwitch });
    setUpdateCompress(false);
    if (!isHolding) {
      setExpandedCompanyRows([]);
    }
  }, [updateCompress, loading, getFlow?.report, checkSwitch, isHolding]);

  const currentFlow = useMemo(() => {
    if (flowCurrentStructure?.report && getFlow?.report) {
      const currentFlowStructure = flowCurrentStructure?.report;
      const incomeTotal = getFlow?.report.INCOME?.find(
        (f) => !f.cashflowCategoryId
      );
      const expenseTotal = getFlow?.report.EXPENSE?.find(
        (f) => !f.cashflowCategoryId
      );
      currentFlowStructure?.INCOME.filter((f) => f.cashflowCategoryId).push(
        incomeTotal
      );
      currentFlowStructure?.EXPENSE.filter((f) => f.cashflowCategoryId).push(
        expenseTotal
      );

      return currentFlowStructure;
    }

    return {};
  }, [flowCurrentStructure?.report, getFlow?.report, getFlow?.companyId]);

  if (loading) {
    return (
      <div className="containerTable">
        <div className="flowSpinner flowLoading">
          <LoadingSpinner height="60px" afterHeight="60px" afterWidth="60px" content={loadingMsg} />
        </div>
        <div
          className="containerTable__cardPreLoader"
          style={{ height: "68vh" }}
        ></div>
      </div>
    );
  }

  const openOrCloseCategory = async (e, selectedArea) => {
    if (!areasSelectedOnce.includes(selectedArea?.cashflowCategoryId)) {
      setAreasSelectedOnce([
        ...areasSelectedOnce,
        selectedArea?.cashflowCategoryId,
      ]);
    }

    if (e.target.classList.contains("customRotateFC")) {
      const companyCompressed = compressedAreas?.map((company) => {
        if (company?.companyId === selectedCompany?.id) {
          const filteredAreas = company?.cashflowCategories?.filter(
            (category) => category !== selectedArea?.cashflowCategoryId
          );
          return { ...company, cashflowCategories: filteredAreas };
        }
        return { ...company };
      });

      const exist = companyCompressed?.find(
        (f) => f?.companyId === selectedCompany?.id
      );

      if (!exist)
        companyCompressed?.push({
          companyId: selectedCompany?.id,
          cashflowCategories: [],
        });

      dispatch(UpdateCashflowCategories(companyCompressed));
      setCompressedAreas(companyCompressed);
      setExpandedCompanyRows([]);

      const newData = companyCompressed?.find(
        (f) => f?.companyId === selectedCompany?.id
      );

      await updateCashflowCollapsed({
        companyId: newData?.companyId,
        categoryId: newData?.cashflowCategories,
      });

      return e.target.classList.remove("customRotateFC");
    }

    const companyCompressed = compressedAreas?.map((company) => {
      if (company?.companyId === selectedCompany?.id) {
        const newCategories = company?.cashflowCategories;
        newCategories?.push(selectedArea?.cashflowCategoryId);
        return { ...company };
      }
      return { ...company };
    });

    const exist = companyCompressed?.find(
      (f) => f?.companyId === selectedCompany?.id
    );

    if (!exist)
      companyCompressed?.push({
        companyId: selectedCompany?.id,
        cashflowCategories: [selectedArea?.cashflowCategoryId],
      });

    dispatch(UpdateCashflowCategories(companyCompressed));
    setCompressedAreas(companyCompressed);

    const newData = companyCompressed?.find(
      (f) => f?.companyId === selectedCompany?.id
    );

    await updateCashflowCollapsed({
      companyId: newData?.companyId,
      categoryId: newData?.cashflowCategories,
    });

    return e.target.classList.add("customRotateFC");
  };

  const openOrCloseArea = async (areaKey) => {
    if (expandedCompanyRows.includes(areaKey)) {
      setExpandedCompanyRows(expandedCompanyRows.filter((id) => id !== areaKey));
      return;
    }
    setExpandedCompanyRows([...expandedCompanyRows, areaKey]);
  };

  const openOrCloseHoldingCategory = (selectedArea) => {
    const catId = selectedArea?.cashflowCategoryId?.toString();
    if (!areasSelectedOnce.includes(selectedArea?.cashflowCategoryId)) {
      setAreasSelectedOnce([
        ...areasSelectedOnce,
        selectedArea?.cashflowCategoryId,
      ]);
    }
    setExpandedHoldingCategories(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  const openFieldDetail = (
    value,
    { cashflowAreaId, isIncome, label, companiesValues, detailCompanyId, detailCompanyName },
    index,
    cashFlowCategoryId,
    reportType
  ) => {
    setReportType(reportType);
    setSelectedCashFlowAreaId(cashflowAreaId);
    setSelectedCashFlowCategoryId(cashFlowCategoryId);
    setSelectedIndex(index);

    setNameSelected(label);

    // const diff = moment(endDate).month() - moment(startDate).month() - 1
    const origin = moment(value?.start)
      .add(6, "hour")
      .subtract(1, view)
      .startOf(view)
      .set("hours", 0)
      .set("minutes", 0)
      .set("seconds", 0)
      .format("YYYY-MM-DDTHH:mm:ss.SSS");

    // const final = moment(value.end)
    //   .add(2, view) // aqui iba el diff
    //   .endOf(view)
    //   .set('hours', 23)
    //   .set('minutes', 59)
    //   .set('seconds', 59)
    //   .format('YYYY-MM-DDTHH:mm:ss.SSS')

    setStartDate(origin);
    // setEndDate(final)
    setSelectedRow(`${cashflowAreaId}-cell${index}`);

    const companyBreakdown = Array.isArray(companiesValues)
      ? companiesValues.map((company) => ({
          companyId: company?.companyId,
          companyName: company?.name,
          value: company?.values?.[index]?.fieldValue,
        }))
      : [];

    return dispatch(
      importOpenSidebar({
        show: true,
        fieldDetail: true,
        editData: {
          value,
          cashflowAreaId,
          isReceived: !isIncome,
          companyId: detailCompanyId,
          detailCompanyName,
          companyBreakdown,
          isHoldingConsolidated: isHolding && !detailCompanyId,
        },
      })
    );
  };

  const openBankDetail = (open, companyIdForDetail) => {
    if (!open) return;
    const allHoldingIds = (holdingCompanyIds || []).map(String).filter(Boolean);
    const scopedId = companyIdForDetail ? String(companyIdForDetail) : null;
    const companyIds = scopedId
      ? [scopedId]
      : allHoldingIds.length > 1
        ? allHoldingIds
        : [];
    return dispatch(
      importOpenSidebar({
        show: true,
        initialBalance: true,
        editData: companyIds.length > 0 ? { companyIds } : {},
      })
    );
  };

  if (!getFlow?.report || !Array.isArray(getFlow?.periods)) {
    return (
      <div className="containerTable">
        <div className="flowSpinner flowLoading">
          <LoadingSpinner
            height="60px"
            afterHeight="60px"
            afterWidth="60px"
            content={loadingMsg || "Cargando..."}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flowTableScroll flowTableScroll--holding" style={{ height: tableHeight - 225 }}>
      {onScreenshotCapture && (
        <FlowScreenshotButton onCapture={onScreenshotCapture} />
      )}
      <div className="flowTableWrapper" style={{ backgroundColor: "white" }} ref={screenshotRef}>
        <Table celled basic="very" className="flowTableFit">
          <Table.Header id="table" className="stickyTableHeader">
            <Table.Row>
              {isHolding ? (
                <Table.HeaderCell />
              ) : (
                <Table.HeaderCell>
                  <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-end">
                    <Icon
                      circular
                      name="angle left"
                      size="small"
                      className="tw-cursor-pointer"
                      onClick={less}
                    />
                  </div>
                </Table.HeaderCell>
              )}
              {getFlow.periods.map((period, index) => {
                if (period.start >= startDate && period.end <= endDate) {
                  let upper = "";
                  let lower = "";

                  if (getFlow.primaryView == VIEW_TYPE.month) {
                    upper = `${capitalize(
                      moment(period.start)
                        .add(1, "day")
                        .format("MMMM")
                        .replace(".", "")
                    )}`;
                    lower = `${moment(period.start)
                      .add(6, "hour")
                      .format("YYYY")}`;
                  }
                  if (getFlow.primaryView == VIEW_TYPE.week) {
                    upper = `Semana ${moment(period.start).format("DD")}`;
                    lower = `de ${capitalize(
                      moment(period.start).format("MMMM")
                    )}`;
                  }
                  if (getFlow.primaryView == VIEW_TYPE.day) {
                    upper = `${moment(period.start).format("dddd")} ${moment(
                      period?.start
                    ).format("DD")}`;
                    lower = `de ${capitalize(
                      moment(period.start).format("MMMM")
                    )}`;
                  }

                  return (
                    <Table.HeaderCell
                      key={`flowPeriod-${index}`}
                      className="monthTitle"
                    >
                      <div className="d-flex flex-column">
                        <div className="newTitle">{upper}</div>
                        <div className="newTitle">{lower}</div>
                      </div>
                    </Table.HeaderCell>
                  );
                }
              })}

              <Table.HeaderCell className="monthTitleTotal">
                <label className="newTitle d-flex justify-content-center" style={{ color: "#ffffff" }}>
                  PROYECTADO
                </label>
              </Table.HeaderCell>

              {!isHolding && (
                <Table.HeaderCell>
                  <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
                    <Icon
                      circular
                      name="angle right"
                      size="small"
                      className="tw-cursor-pointer"
                      onClick={plus}
                    />
                  </div>
                </Table.HeaderCell>
              )}
            </Table.Row>
          </Table.Header>

          <Table.Body className="flowBody">
            <Table.Row className="flowSectionHeaderRow">
              <Table.Cell
                colSpan={1 + getFlow.periods.filter((p) => p.start >= startDate && p.end <= endDate).length + 1}
                className="flowSectionHeaderCell"
              >
                <strong>SALDO INICIAL</strong>
              </Table.Cell>
            </Table.Row>

            {getFlow.report.RESUME.map((field, row) => {
              if (row == 0) return;

              const initialBalanceExpandKey = `saldo-inicial-${(field?.companiesValues || [])
                .map((company) => company?.companyId)
                .filter(Boolean)
                .sort()
                .join("-") || "holding"}`;
              const hasCompanyBreakdown = Boolean(field?.companiesValues?.length);
              const isInitialBalanceExpanded = expandedCompanyRows?.includes(initialBalanceExpandKey);

              return (
                <React.Fragment key={`${field.label}-fragment`}>
                  <Table.Row key={`${field.label}-row`} className="saldoInicialRow">
                    <Table.Cell
                      className="px-2 totalCell"
                      key={`${field.label}-label`}
                      width={maxRowNameWidth}
                    >
                      <section
                        className="tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-2"
                      >
                        <span
                          className="holding-saldo-inicial-label tw-cursor-pointer"
                          onClick={() => {
                            if (hasCompanyBreakdown) openOrCloseArea(initialBalanceExpandKey);
                          }}
                        >
                          <span className="holding-saldo-inicial-label__title">
                            {field.label}
                          </span>
                          {hasCompanyBreakdown && (
                            <span className="holding-saldo-inicial-label__count">
                              ({field.companiesValues.length} empresas)
                            </span>
                          )}
                        </span>
                        <span className="holding-saldo-inicial-actions">
                          <Popup
                            content="Ver detalle por cuenta bancaria"
                            position="left center"
                            size="tiny"
                            trigger={
                              <Icon
                                circular
                                className="BankIconCashflow holding-saldo-inicial-bank-btn"
                                name="university"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openBankDetail(true);
                                }}
                              />
                            }
                          />
                          {hasCompanyBreakdown && (
                            <Icon
                              name="angle right"
                              className={`clicableAngleFc ${isInitialBalanceExpanded ? "customRotateFC" : ""}`}
                              onClick={() => openOrCloseArea(initialBalanceExpandKey)}
                            />
                          )}
                        </span>
                      </section>
                    </Table.Cell>
                    {field.values.map((value, index) => {
                      if (
                        (value?.start >= startDate && value?.end <= endDate) ||
                        value?.isTotal
                      ) {
                        return (
                          <Table.Cell
                            key={`${field.label}-cell${index}`}
                            className={`amountCell totalCell px-2 ${value?.isPast && "fcPasado"}`}
                            style={{ borderTop: "1px solid #475569" }}
                          >
                            {value.fieldValue === 0 ? (
                              <div className="w-100 text-center">-</div>
                            ) : (
                              <div className="d-flex align-items-center">
                                <span
                                  className={`${value?.fieldValue < 0 && "text-danger"
                                    } d-flex align-items-center`}
                                >
                                  {CurrencyIcon[value.currency]}{" "}
                                </span>
                                <span
                                  className={`${value?.fieldValue < 0 && "text-danger"
                                    } text-end w-100`}
                                >
                                  {formatMoneyWithCurrency(
                                    Math.round(value?.fieldValue),
                                    value.currency,
                                    false
                                  ) || 0}
                                </span>
                              </div>
                            )}
                          </Table.Cell>
                        );
                      }
                    })}
                  </Table.Row>
                  {hasCompanyBreakdown &&
                    isInitialBalanceExpanded &&
                    field.companiesValues.map((company, companyIndex) => (
                        <Table.Row
                          key={`saldo-inicial-${company.companyId}-row${companyIndex}`}
                          className="openedArea holding-initial-balance-company-row"
                          style={{ position: "relative" }}
                        >
                          <Table.Cell
                            className="incomeOutcomeRow d-flex justify-content-between company-subrow-income holding-company-subrow-label"
                            width={maxRowNameWidth}
                          >
                            <div className="tw-w-full tw-pl-10 tw-text-slate-600 tw-flex tw-items-center tw-justify-between tw-gap-2">
                              <span>{company.name}</span>
                              <Popup
                                content={`Ver cuentas bancarias de ${company.name}`}
                                position="left center"
                                size="tiny"
                                trigger={
                                  <Icon
                                    circular
                                    className="BankIconCashflow holding-saldo-inicial-bank-btn"
                                    name="university"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openBankDetail(true, company.companyId);
                                    }}
                                  />
                                }
                              />
                            </div>
                          </Table.Cell>
                          {(company.values || []).map((value, valueIndex) => {
                            if (
                              (value?.start >= startDate && value?.end <= endDate) ||
                              value?.isTotal
                            ) {
                              return (
                                <Table.Cell
                                  key={`saldo-inicial-${company.companyId}-cell${valueIndex}`}
                                  className={`amountCell amount incomeOutcomeMoneyRow ${value?.isPast && "fcPasado"
                                    } company-subrow-income holding-company-subrow-value`}
                                >
                                  {value.fieldValue === 0 ? (
                                    <div className="w-100 text-center">-</div>
                                  ) : (
                                    <div className="d-flex align-items-center">
                                      <span
                                        className={`${value?.fieldValue < 0 ? "text-danger" : ""} d-flex align-items-center`}
                                      >
                                        {CurrencyIcon[value.currency]}{" "}
                                      </span>
                                      <span
                                        className={`${value?.fieldValue < 0 ? "text-danger" : ""} text-end w-100`}
                                      >
                                        {formatMoneyWithCurrency(
                                          Math.round(value?.fieldValue),
                                          value.currency,
                                          false
                                        ) || 0}
                                      </span>
                                    </div>
                                  )}
                                </Table.Cell>
                              );
                            }
                            return null;
                          })}
                        </Table.Row>
                      ))}
                </React.Fragment>
              );
            })}

            <Table.Row className="flowSectionHeaderRow">
              <Table.Cell
                colSpan={1 + getFlow.periods.filter((p) => p.start >= startDate && p.end <= endDate).length + 1}
                className="flowSectionHeaderCell"
              >
                <strong>INGRESOS</strong>
              </Table.Cell>
            </Table.Row>

            {getFlow?.report?.INCOME.map((income, index) => {
              const currentCategory = getFlow.report.INCOME?.find(
                (f) => f.cashflowCategoryId === income.cashflowCategoryId
              );
              return (
                <>
                  {(income.isVisible || showAll) && (
                    <Table.Row
                      key={`${income.cashflowCategoryId}`}
                      className={`position-relative ${!income.cashflowCategoryId ? "incomeTotalRow" : ""}`}
                    >
                      <Table.Cell
                        key={`${income.cashflowCategoryId}-label`}
                        className={`${income.cashflowCategoryId
                          ? "subTotal"
                          : "categoryTotal"
                          } incomeOutcomeRow`}
                      >
                        <div className="tw-flex tw-gap-1 tw-items-center">
                          <span>{income.label}</span>
                          {income.cashflowCategoryId && (
                            <Icon
                              name="angle right"
                              className={`clicableAngleFc ${isHolding
                                ? expandedHoldingCategories.includes(income.cashflowCategoryId?.toString()) && "customRotateFC"
                                : compressedAreas?.find((f) => f?.companyId === selectedCompany?.id)?.cashflowCategories?.includes(income.cashflowCategoryId) && "customRotateFC"
                              } !tw-h-auto`}
                              onClick={(e) => isHolding ? openOrCloseHoldingCategory(income) : openOrCloseCategory(e, income)}
                            />
                          )}
                        </div>
                      </Table.Cell>

                      {currentCategory.values.map((value, index) => {
                        if (
                          (value.start >= startDate && value.end <= endDate) ||
                          value.isTotal
                        ) {
                          return (
                            <Table.Cell
                              key={`${income.cashflowCategoryId}-cell${index}`}
                              className={`amountCell ${value.isPast && "fcPasado"
                                } ${value.isTotal
                                  ? "totalCell"
                                  : income?.cashflowCategoryId
                                    ? "subTotal incomeOutcomeMoneyRow"
                                    : "categoryTotal incomeOutcomeMoneyRow"
                                }`}
                            >
                              {value.fieldValue === 0 ? (
                                <div className="w-100 text-center">-</div>
                              ) : (
                                <div className="d-flex align-items-center">
                                  <span className="d-flex align-items-center">
                                    <Popup
                                      content={
                                        "Hay pagos atrasados en esta área"
                                      }
                                      position="left center"
                                      size="tiny"
                                      trigger={
                                        <Icon
                                          circular
                                          size="tiny"
                                          className={`${"exclamationIcon"} mr-2`}
                                          name="exclamation"
                                          style={{
                                            visibility: "hidden",
                                          }}
                                        />
                                      }
                                    />
                                    {CurrencyIcon[value.currency]}{" "}
                                  </span>
                                  <span className="text-end w-100">
                                    {formatMoneyWithCurrency(
                                      Math.round(value.fieldValue),
                                      value.currency,
                                      false
                                    ) || 0}
                                  </span>
                                </div>
                              )}
                            </Table.Cell>
                          );
                        }
                      })}
                    </Table.Row>
                  )}

                  {
                    income.fields.map((field, index) => {
                      const currentField = currentCategory?.fields?.find(
                        (f) => f.cashflowAreaId === field.cashflowAreaId
                      );
                      const deferredConflict = findDeferredAreaConflict(
                        "INCOME",
                        income.cashflowCategoryId,
                        field
                      );
                      const isCatExpanded = isHolding
                        ? expandedHoldingCategories.includes(income.cashflowCategoryId?.toString())
                        : !!compressedAreas?.find((f) => f?.companyId === selectedCompany?.id)?.cashflowCategories?.includes(income.cashflowCategoryId);
                      const companyKey = (field?.companiesValues || [])
                        .map((company) => company?.companyId)
                        .filter(Boolean)
                        .sort()
                        .join("-");
                      const areaExpandKey = `${companyKey}-${field.cashflowAreaId || "no-area-id"}`;
                      const className = `${!isCatExpanded && "colapsedArea"} ${isCatExpanded && areasSelectedOnce.includes(income.cashflowCategoryId) && "openedArea"} ${deferredConflict ? "holding-area-conflict-row" : ""}`;

                      return (
                        (showAll || !field.isHidden) && (
                          <>
                            <Table.Row
                              key={`${field.cashflowAreaId}-row${index}`}
                              className={className}
                              style={{ position: "relative" }}
                            >
                              {(field.isVisible || showAll) && (
                                <Table.Cell
                                  key={`${field.cashflowAreaId}-label`}
                                  className={`incomeOutcomeRow fcAreaLabel`}
                                  width={maxRowNameWidth}
                                >
                                  <section className="tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-2 tw-pl-4">
                                    <div className="tw-flex tw-items-center tw-gap-1 tw-min-w-0">
                                      <span className="tw-truncate" title={field.label}>
                                        {field.label}
                                      </span>
                                      {field.cashflowAreaId && (
                                        <Icon
                                          name="angle right"
                                          className={`clicableAngleFc ${expandedCompanyRows
                                            ?.includes(areaExpandKey) ? 'customRotateFC' : ''}`}
                                          onClick={() => openOrCloseArea(areaExpandKey)}
                                        />
                                      )}
                                    </div>

                                    <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                                      {renderDeferredConflictWarning(deferredConflict)}
                                      {showAll && field.isHidden && (
                                        <Popup
                                          content="Área oculta"
                                          position="top center"
                                          size="mini"
                                          inverted
                                          trigger={
                                            <Icon
                                              name="eye slash"
                                              style={{ color: "#acacac" }}
                                            />
                                          }
                                        />
                                      )}
                                      {
                                        field?.mergedFrom && (
                                          <Popup
                                            on="click"
                                            position="top center"
                                            content={renderMergedAreaPopupContent(field?.mergedFrom)}
                                            trigger={
                                              <img
                                                src={mergedIcon}
                                                className="tw-text-pepper-500 tw-w-[18px] tw-h-[18px] tw-cursor-pointer"
                                              />
                                            }
                                          />
                                        )
                                      }
                                    </div>
                                  </section>
                                </Table.Cell>
                              )}
                              {field.values.map((value, index) => {
                                if (
                                  ((value.start >= startDate &&
                                    value.end <= endDate) ||
                                    value.isTotal) &&
                                  (field.isVisible || showAll)
                                ) {
                                  const isCellSelected =
                                    selectedRow ==
                                    `${field.cashflowAreaId}-cell${index}`;

                                  return (
                                    <Table.Cell
                                      key={`${field.cashflowAreaId}-cell${index}`}
                                      onClick={() =>
                                        openFieldDetail(
                                          value,
                                          { ...field, companiesValues: field?.companiesValues },
                                          index,
                                          currentCategory.cashflowCategoryId,
                                          "INCOME"
                                        )
                                      }
                                      className={`amountCell ${value.isTotal
                                        ? "totalCell"
                                        : "amount incomeOutcomeMoneyRow"
                                        } ${value.isLate && "useDelayesd"} ${value.isPast && "fcPasado"
                                        } ${isCellSelected ? "SelectedRow" : ""}`}
                                    >
                                      {value.fieldValue === 0 ? (
                                        <div className="w-100 text-center">-</div>
                                      ) : (
                                        <div className="d-flex align-items-center">
                                          <span className="d-flex align-items-center">
                                            <Popup
                                              content={
                                                field.isProjected
                                                  ? "Ya tienes DTEs, tal vez quieras bajar la proyección"
                                                  : "Hay pagos atrasados en esta área"
                                              }
                                              position="left center"
                                              size="tiny"
                                              trigger={
                                                <Icon
                                                  circular
                                                  size="tiny"
                                                  className={`${field.isProjected
                                                    ? "exclamationIconProjection"
                                                    : "exclamationIcon"
                                                    } mr-2`}
                                                  name="exclamation"
                                                  style={{
                                                    visibility: value.isLate
                                                      ? "visible"
                                                      : "hidden",
                                                  }}
                                                />
                                              }
                                            />
                                            {CurrencyIcon[value.currency]}{" "}
                                          </span>
                                          <span className="text-end w-100">
                                            {formatMoneyWithCurrency(
                                              Math.round(value.fieldValue),
                                              value.currency,
                                              "",
                                              false
                                            ) || 0}
                                          </span>
                                        </div>
                                      )}
                                    </Table.Cell>
                                  );
                                }
                              })}
                            </Table.Row>
                            {
                              field?.companiesValues?.map((company, index) => {
                                const isExpanded = expandedCompanyRows?.includes(areaExpandKey);
                                const companyRowClass = isExpanded ? "openedArea" : "colapsedArea";

                                return (
                                  // (showAll || company.isVisible) &&
                                  (
                                    <Table.Row
                                      key={`${company.companyId}-row${index}`}
                                      className={companyRowClass}
                                      style={{ position: "relative" }}
                                    >
                                      <Table.Cell
                                        key={`${company.companyId}-label`}
                                        className={`incomeOutcomeRow d-flex justify-content-between company-subrow-income holding-company-subrow-label`}
                                        width={maxRowNameWidth}
                                      >
                                        <div className="tw-w-full tw-pl-10">
                                          {company.name}
                                        </div>
                                      </Table.Cell>

                                      {company.values.map((value, index) => {
                                        if (
                                          ((value.start >= startDate &&
                                            value.end <= endDate) ||
                                            value.isTotal) &&
                                          (field.isVisible || showAll)
                                        ) {
                                          const isCellSelected =
                                            selectedRow ==
                                            `${field.cashflowAreaId}-cell${index}`;

                                          return (
                                            <Table.Cell
                                              key={`${field.cashflowAreaId}-cell${index}`}
                                              onClick={() =>
                                                openFieldDetail(
                                                  value,
                                                  {
                                                    ...field,
                                                    detailCompanyId: company?.companyId,
                                                    detailCompanyName: company?.name,
                                                  },
                                                  index,
                                                  currentCategory.cashflowCategoryId,
                                                  "INCOME"
                                                )
                                              }
                                              className={`amountCell ${value.isTotal
                                                ? "totalCell"
                                                : "amount incomeOutcomeMoneyRow"
                                                } ${value.isLate && "useDelayesd"} ${value.isPast && "fcPasado"
                                                } ${isCellSelected ? "SelectedRow" : ""} company-subrow-income holding-company-subrow-value`}
                                            >
                                              {value.fieldValue === 0 ? (
                                                <div className="w-100 text-center">-</div>
                                              ) : (
                                                <div className="d-flex align-items-center">
                                                  <span className="d-flex align-items-center">
                                                    <Popup
                                                      content={
                                                        field.isProjected
                                                          ? "Ya tienes DTEs, tal vez quieras bajar la proyección"
                                                          : "Hay pagos atrasados en esta área"
                                                      }
                                                      position="left center"
                                                      size="tiny"
                                                      trigger={
                                                        <Icon
                                                          circular
                                                          size="tiny"
                                                          className={`${field.isProjected
                                                            ? "exclamationIconProjection"
                                                            : "exclamationIcon"
                                                            } mr-2`}
                                                          name="exclamation"
                                                          style={{
                                                            visibility: value.isLate
                                                              ? "visible"
                                                              : "hidden",
                                                          }}
                                                        />
                                                      }
                                                    />
                                                    {CurrencyIcon[value.currency]}{" "}
                                                  </span>
                                                  <span className="text-end w-100">
                                                    {formatMoneyWithCurrency(
                                                      Math.round(value.fieldValue),
                                                      value.currency,
                                                      "",
                                                      false
                                                    ) || 0}
                                                  </span>
                                                </div>
                                              )}
                                            </Table.Cell>
                                          );
                                        }
                                      })}
                                    </Table.Row>
                                  )

                                );
                              })
                            }
                          </>

                        )
                      );
                    })}
                </>
              );
            })}

            <Table.Row className="flowSectionHeaderRow">
              <Table.Cell
                colSpan={1 + getFlow.periods.filter((p) => p.start >= startDate && p.end <= endDate).length + 1}
                className="flowSectionHeaderCell"
              >
                <strong>EGRESOS</strong>
              </Table.Cell>
            </Table.Row>

            {getFlow.report.EXPENSE.map((expense, index) => {
              const currentCategory = getFlow.report.EXPENSE?.find(
                (f) => f.cashflowCategoryId === expense.cashflowCategoryId
              );
              return (
                <>
                  {(expense.isVisible || showAll) && (
                    <Table.Row
                      key={`${expense.cashflowCategoryId}`}
                      className={`position-relative ${!expense.cashflowCategoryId ? "expenseTotalRow" : ""}`}
                    >
                      <Table.Cell
                        key={`${expense.cashflowCategoryId}-label`}
                        className={`${expense?.cashflowCategoryId
                          ? "subTotal"
                          : "outCategoryTotal"
                          } incomeOutcomeRow`}
                        width={maxRowNameWidth}
                      >
                        <div className="tw-flex tw-gap-1 tw-items-center">
                          <span>{expense?.label}</span>
                          {expense?.cashflowCategoryId && (
                            <Icon
                              name="angle right"
                              className={`clicableAngleFc ${isHolding
                                ? expandedHoldingCategories.includes(expense.cashflowCategoryId?.toString()) && "customRotateFC"
                                : compressedAreas?.find((f) => f?.companyId === selectedCompany?.id)?.cashflowCategories?.includes(expense.cashflowCategoryId) && "customRotateFC"
                              } !tw-h-auto`}
                              onClick={(e) => isHolding ? openOrCloseHoldingCategory(expense) : openOrCloseCategory(e, expense)}
                            />
                          )}
                        </div>
                      </Table.Cell>
                      {expense?.values?.map((value, index) => {
                        if (
                          (value.start >= startDate && value.end <= endDate) ||
                          value.isTotal
                        )
                          return (
                            <Table.Cell
                              key={`${expense.cashflowCategoryId}-cell${index}`}
                              className={`amountCell ${value.isPast && "fcPasado"
                                } ${value.isTotal
                                  ? "totalCell"
                                  : expense?.cashflowCategoryId
                                    ? "subTotal incomeOutcomeMoneyRow"
                                    : "outCategoryTotal incomeOutcomeMoneyRow"
                                }`}
                            >
                              {value.fieldValue === 0 ? (
                                <div className="w-100 text-center">-</div>
                              ) : (
                                <div className="d-flex align-items-center">
                                  <span className="d-flex align-items-center">
                                    <Popup
                                      content={
                                        "Hay pagos atrasados en esta área"
                                      }
                                      position="left center"
                                      size="tiny"
                                      trigger={
                                        <Icon
                                          circular
                                          size="tiny"
                                          className={`${"exclamationIcon"} mr-2`}
                                          name="exclamation"
                                          style={{
                                            visibility: "hidden",
                                          }}
                                        />
                                      }
                                    />
                                    {CurrencyIcon[value.currency]}{" "}
                                  </span>
                                  <span className="text-end w-100">
                                    {formatMoneyWithCurrency(
                                      Math.round(value.fieldValue),
                                      value.currency,
                                      false
                                    ) || 0}
                                  </span>
                                </div>
                              )}
                            </Table.Cell>
                          );
                      })}
                    </Table.Row>
                  )}

                  {expense.fields.map((field, index) => {

                    const currentField = currentCategory?.fields?.find(
                      (f) => f.cashflowAreaId === field.cashflowAreaId
                    );
                    const deferredConflict = findDeferredAreaConflict(
                      "EXPENSE",
                      expense.cashflowCategoryId,
                      field
                    );
                    const isCatExpandedExp = isHolding
                      ? expandedHoldingCategories.includes(expense.cashflowCategoryId?.toString())
                      : !!compressedAreas?.find((f) => f?.companyId === selectedCompany?.id)?.cashflowCategories?.includes(expense.cashflowCategoryId);
                    const companyKey = (field?.companiesValues || [])
                      .map((company) => company?.companyId)
                      .filter(Boolean)
                      .sort()
                      .join("-");
                    const areaExpandKey = `${companyKey}-${field.cashflowAreaId || "no-area-id"}`;
                    const className = `${!isCatExpandedExp && "colapsedArea"} ${isCatExpandedExp && areasSelectedOnce.includes(expense.cashflowCategoryId) && "openedArea"} ${deferredConflict ? "holding-area-conflict-row" : ""}`;

                    return (
                      (showAll || !field.isHidden) && (
                        <>
                          <Table.Row
                            key={`${field.cashflowAreaId}-row${index}`}
                            className={className}
                            style={{ position: "relative" }}
                          >
                            {(field.isVisible || showAll) && (
                              <Table.Cell
                                key={`${field.cashflowAreaId}-label`}
                                className={`incomeOutcomeRow fcAreaLabel`}
                                width={maxRowNameWidth}
                              >
                                <section className="tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-2 tw-pl-4">
                                  <div className="tw-flex tw-items-center tw-gap-1 tw-min-w-0">
                                    <span className="tw-truncate" title={field.label}>
                                      {field.label}
                                    </span>
                                    {field.cashflowAreaId && (
                                      <Icon
                                        name="angle right"
                                        className={`clicableAngleFc ${expandedCompanyRows
                                          ?.includes(areaExpandKey) ? 'customRotateFC' : ''}`}
                                        onClick={() => openOrCloseArea(areaExpandKey)}
                                      />
                                    )}
                                  </div>
                                  <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                                    {renderDeferredConflictWarning(deferredConflict)}
                                    {showAll && field.isHidden && (
                                      <Popup
                                        content="Área oculta"
                                        position="top center"
                                        size="mini"
                                        inverted
                                        trigger={
                                          <Icon
                                            name="eye slash"
                                            style={{ color: "#acacac" }}
                                          />
                                        }
                                      />
                                    )}
                                    {
                                      field?.mergedFrom && (
                                        <Popup
                                          on="click"
                                          position="top center"
                                          content={renderMergedAreaPopupContent(field?.mergedFrom)}
                                          trigger={
                                            <img
                                              src={mergedIcon}
                                              className="tw-text-pepper-500 tw-w-[18px] tw-h-[18px] tw-cursor-pointer"
                                            />
                                          }
                                        />
                                      )
                                    }
                                  </div>
                                </section>
                              </Table.Cell>
                            )}
                            {field.values.map((value, index) => {
                              if (
                                ((value.start >= startDate &&
                                  value.end <= endDate) ||
                                  value.isTotal) &&
                                (field.isVisible || showAll)
                              ) {
                                const isCellSelected =
                                  selectedRow ==
                                  `${field.cashflowAreaId}-cell${index}`;

                                return (
                                  <Table.Cell
                                    key={`${field.cashflowAreaId}-cell${index}`}
                                    onClick={() =>
                                      openFieldDetail(
                                        value,
                                        { ...field, companiesValues: field?.companiesValues },
                                        index,
                                        currentCategory.cashflowCategoryId,
                                        "EXPENSE"
                                      )
                                    }
                                    className={`amountCell ${value.isTotal
                                      ? "totalCell"
                                      : "amount incomeOutcomeMoneyRow"
                                      } ${value.isLate && "useDelayesd"} ${value.isPast && "fcPasado"
                                      } ${isCellSelected ? "SelectedRow" : ""}`}
                                  >
                                    {value.fieldValue === 0 ? (
                                      <div className="w-100 text-center">-</div>
                                    ) : (
                                      <div className="d-flex align-items-center">
                                        <span className="d-flex align-items-center">
                                          <Popup
                                            content={
                                              field.isProjected
                                                ? "Ya tienes DTEs, tal vez quieras bajar la proyección"
                                                : "Hay pagos atrasados en esta área"
                                            }
                                            position="left center"
                                            size="tiny"
                                            trigger={
                                              <Icon
                                                circular
                                                size="tiny"
                                                className={`${field.isProjected
                                                  ? "exclamationIconProjection"
                                                  : "exclamationIcon"
                                                  } mr-2`}
                                                name="exclamation"
                                                style={{
                                                  visibility: value.isLate
                                                    ? "visible"
                                                    : "hidden",
                                                }}
                                              />
                                            }
                                          />
                                          {CurrencyIcon[value.currency]}{" "}
                                        </span>
                                        <span className="text-end w-100">
                                          {formatMoneyWithCurrency(
                                            Math.round(value.fieldValue),
                                            value.currency,
                                            "",
                                            false
                                          ) || 0}
                                        </span>
                                      </div>
                                    )}
                                  </Table.Cell>
                                );
                              }
                            })}
                          </Table.Row>
                          {
                            field?.companiesValues?.map((company, index) => {
                              const isExpanded = expandedCompanyRows?.includes(areaExpandKey);
                              const companyRowClass = isExpanded ? "openedArea" : "colapsedArea";

                              return (
                                // (showAll || company.isVisible) &&
                                (
                                  <Table.Row
                                    key={`${company.companyId}-row${index}`}
                                    className={companyRowClass}
                                    style={{ position: "relative" }}
                                  >
                                    <Table.Cell
                                      key={`${company.companyId}-label`}
                                      className={`incomeOutcomeRow d-flex justify-content-between company-subrow-expense holding-company-subrow-label`}
                                      width={maxRowNameWidth}
                                    >
                                      <div className="tw-w-full tw-pl-10">
                                        {company.name}
                                      </div>
                                    </Table.Cell>

                                    {company.values.map((value, index) => {
                                      if (
                                        ((value.start >= startDate &&
                                          value.end <= endDate) ||
                                          value.isTotal) &&
                                        (field.isVisible || showAll)
                                      ) {
                                        const isCellSelected =
                                          selectedRow ==
                                          `${field.cashflowAreaId}-cell${index}`;

                                        return (
                                          <Table.Cell
                                            key={`${field.cashflowAreaId}-cell${index}`}
                                            onClick={() =>
                                              openFieldDetail(
                                                value,
                                                {
                                                  ...field,
                                                  detailCompanyId: company?.companyId,
                                                  detailCompanyName: company?.name,
                                                },
                                                index,
                                                currentCategory.cashflowCategoryId,
                                                "EXPENSE"
                                              )
                                            }
                                            className={`amountCell ${value.isTotal
                                              ? "totalCell"
                                              : "amount incomeOutcomeMoneyRow"
                                              } ${value.isLate && "useDelayesd"} ${value.isPast && "fcPasado"
                                              } ${isCellSelected ? "SelectedRow" : ""} company-subrow-expense holding-company-subrow-value`}
                                          >
                                            {value.fieldValue === 0 ? (
                                              <div className="w-100 text-center">-</div>
                                            ) : (
                                              <div className="d-flex align-items-center">
                                                <span className="d-flex align-items-center">
                                                  <Popup
                                                    content={
                                                      field.isProjected
                                                        ? "Ya tienes DTEs, tal vez quieras bajar la proyección"
                                                        : "Hay pagos atrasados en esta área"
                                                    }
                                                    position="left center"
                                                    size="tiny"
                                                    trigger={
                                                      <Icon
                                                        circular
                                                        size="tiny"
                                                        className={`${field.isProjected
                                                          ? "exclamationIconProjection"
                                                          : "exclamationIcon"
                                                          } mr-2`}
                                                        name="exclamation"
                                                        style={{
                                                          visibility: value.isLate
                                                            ? "visible"
                                                            : "hidden",
                                                        }}
                                                      />
                                                    }
                                                  />
                                                  {CurrencyIcon[value.currency]}{" "}
                                                </span>
                                                <span className="text-end w-100">
                                                  {formatMoneyWithCurrency(
                                                    Math.round(value.fieldValue),
                                                    value.currency,
                                                    "",
                                                    false
                                                  ) || 0}
                                                </span>
                                              </div>
                                            )}
                                          </Table.Cell>
                                        );
                                      }
                                    })}
                                  </Table.Row>
                                )

                              );
                            })
                          }
                        </>

                      )
                    );
                  })}
                </>
              );
            })}

            <Table.Row className="flowSectionHeaderRow">
              <Table.Cell
                colSpan={1 + getFlow.periods.filter((p) => p.start >= startDate && p.end <= endDate).length + 1}
                className="flowSectionHeaderCell"
              >
                <strong>VARIACIÓN DE CAJA</strong>
              </Table.Cell>
            </Table.Row>

            {getFlow.report.RESUME.map((field, row) => {
              if (row > 0) return;

              return (
                <Table.Row key={`${field.label}-row`}>
                  <Table.Cell
                    className="resumeCell px-2"
                    key={`${field.label}-label`}
                    width={maxRowNameWidth}
                  >
                    {field.label}
                  </Table.Cell>
                  {field.values.map((value, index) => {
                    if (
                      (value?.start >= startDate && value?.end <= endDate) ||
                      value?.isTotal
                    ) {
                      const isResumeClickable =
                        (getFlow.primaryView === VIEW_TYPE.month &&
                          moment(value.start).format("MMYYYY") ===
                          moment().format("MMYYYY")) ||
                        (getFlow.primaryView === VIEW_TYPE.week &&
                          moment(value.start)
                            .startOf(VIEW_TYPE.week)
                            .format() ===
                          moment().startOf(VIEW_TYPE.week).format()) ||
                        (getFlow.primaryView === VIEW_TYPE.day &&
                          moment(value.start).format("DD-MM-YYYY") ===
                          moment().format("DD-MM-YYYY"));

                      return (
                        <Table.Cell
                          key={`${field.label}-cell${index}`}
                          onClick={() =>
                            openBankDetail(isResumeClickable && row > 0)
                          }
                          className={`amountCell ${value?.isTotal ? "totalCell" : "resumeCell"
                            } ${value?.isPast && "fcPasado"} ${field?.label === "Saldo inicial" &&
                              isResumeClickable
                              ? "resumeClickable"
                              : ""
                            }`}
                          style={{ borderTop: "1px solid #0771ad" }}
                        >
                          {value.fieldValue === 0 ? (
                            <div className="w-100 text-center">-</div>
                          ) : (
                            <div className="d-flex align-items-center">
                              <span
                                className={`${value?.fieldValue < 0 && "text-danger"
                                  } d-flex align-items-center`}
                              >
                                <Popup
                                  content={"Ver detalle"}
                                  position="left center"
                                  size="tiny"
                                  trigger={
                                    <Icon
                                      circular
                                      className="BankIconCashflow mr-2"
                                      name="university"
                                      style={{
                                        visibility:
                                          field?.label === "Saldo inicial" &&
                                            isResumeClickable
                                            ? "visible"
                                            : "hidden",
                                      }}
                                    />
                                  }
                                />
                                {CurrencyIcon[value.currency]}{" "}
                              </span>
                              <span
                                className={`${value?.fieldValue < 0 && "text-danger"
                                  } text-end w-100`}
                              >
                                {formatMoneyWithCurrency(
                                  Math.round(value?.fieldValue),
                                  value.currency,
                                  false
                                ) || 0}
                              </span>
                            </div>
                          )}
                        </Table.Cell>
                      );
                    }
                  })}
                </Table.Row>
              );
            })}

            {/* <TableHeader title="TOTAL" /> */}

            {getFlow?.report?.TOTAL?.map((total, index) => {
              return (
                <Table.Row
                  key={`${total.label}-row`}
                // className="stickyTableBottom flowBlueColor"
                >
                  <Table.Cell
                    key={`${total.label}-label`}
                    className="totalCell px-2"
                    width={maxRowNameWidth}
                  >
                    {total.label}
                  </Table.Cell>
                  {total?.values?.map((value, index) => {
                    if (
                      (value?.start >= startDate && value?.end <= endDate) ||
                      value?.isTotal
                    )
                      return (
                        <Table.Cell
                          key={`${total.label}-cell${index}`}
                          className={`amountCell totalCell px-2 ${value?.isPast && "fcPasado"
                            }`}
                        >
                          {value?.fieldValue === 0 ? (
                            <div className="w-100 text-center">-</div>
                          ) : (
                            <div className="d-flex align-items-center">
                              <span
                                className={`${value?.fieldValue < 0 && "text-danger"
                                  } d-flex align-items-center`}
                              >
                                <Popup
                                  content={"Hay pagos atrasados en esta área"}
                                  position="left center"
                                  size="tiny"
                                  trigger={
                                    <Icon
                                      circular
                                      size="tiny"
                                      className={`${"exclamationIcon"} mr-2`}
                                      name="exclamation"
                                      style={{
                                        visibility: "hidden",
                                      }}
                                    />
                                  }
                                />
                                {CurrencyIcon[value.currency]}{" "}
                              </span>
                              <span
                                className={`${value?.fieldValue < 0 && "text-danger"
                                  } text-end w-100`}
                              >
                                {formatMoneyWithCurrency(
                                  Math.round(value?.fieldValue),
                                  value.currency,
                                  false
                                ) || 0}
                              </span>
                            </div>
                          )}
                        </Table.Cell>
                      );
                  })}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    </div>
  );
}
